/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import {
  calculateFeePreview,
  type FeePreviewInput,
} from '../../../helpers/processingFees'

type FeePreviewProps = {
  input: FeePreviewInput
  currencySymbol: string
}

/**
 * Live worked example for the Processing Fees block. Recomputes as the admin
 * types so the screen explains itself. Pure presentation — all arithmetic
 * lives in calculateFeePreview.
 */
const FeePreview = ({ input, currencySymbol }: FeePreviewProps) => {
  const preview = calculateFeePreview(input)
  const money = (value: number) => `${currencySymbol}${value.toFixed(2)}`
  const absorbsProcessing = input.bearer === 'league'

  // Split by which side of the tax line each fee belongs on. Keyed off the
  // fee's identity rather than its position, so this holds whatever order
  // calculateFeePreview returns.
  const taxedFeeLines = preview.feeLines.filter(
    (line) => line.key !== 'payment_processing_fee'
  )
  const untaxedFeeLines = preview.feeLines.filter(
    (line) => line.key === 'payment_processing_fee'
  )

  return (
    <div css={containerStyle}>
      <Text variant='captionXSmall' weight='semiBold' color={colors.NEUTRAL_50}>
        {capitalizeWords(
          `Example — ${money(input.baseCost)} Registration`
        ).toUpperCase()}
      </Text>

      <div css={rowStyle}>
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {capitalizeWords('Registration Cost')}
        </Text>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_30}
        >
          {money(preview.invoiceSubtotal)}
        </Text>
      </div>

      {/* Ordered to mirror how the money actually stacks: the league fee is
          league revenue and IS taxed, so it sits above the tax line; the
          payment processing fee is not taxed, so it sits below it. */}
      {taxedFeeLines.map((line) => (
        <div css={rowStyle} key={line.key}>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.PRIMARY}
          >
            {capitalizeWords(line.label)}
          </Text>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.PRIMARY}
          >
            {money(line.amount)}
          </Text>
        </div>
      ))}

      {preview.tax > 0 ? (
        <div css={rowStyle}>
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {capitalizeWords(`Sales Tax ${input.taxRatePercent}%`)}
          </Text>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_30}
          >
            {money(preview.tax)}
          </Text>
        </div>
      ) : null}

      {untaxedFeeLines.map((line) => (
        <div css={rowStyle} key={line.key}>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.PRIMARY}
          >
            {capitalizeWords(line.label)}
          </Text>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.PRIMARY}
          >
            {money(line.amount)}
          </Text>
        </div>
      ))}

      <div css={dividerStyle} />

      <div css={rowStyle}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords('Registrant Pays')}
        </Text>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {money(preview.registrantPays)}
        </Text>
      </div>

      {absorbsProcessing ? (
        <>
          <div css={rowStyle}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {capitalizeWords('Payment Processing (From Your Payout)')}
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {`−${money(preview.processingDeduction)}`}
            </Text>
          </div>
          <div css={rowStyle}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {capitalizeWords('Your Payout')}
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {money(preview.yourPayout)}
            </Text>
          </div>
        </>
      ) : (
        <div css={rowStyle}>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {capitalizeWords('Your Payout')}
          </Text>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {money(preview.yourPayout)}
          </Text>
        </div>
      )}
    </div>
  )
}

export default FeePreview

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
`

const rowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  gap: 16px;
`

const dividerStyle = css`
  align-self: stretch;
  height: 1px;
  background-color: ${colors.NEUTRAL_100};
`
